import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import styles from './Page.module.css';
import s from './LocalEstoque.module.css';

const VAZIO = { nome: '', sigla: '', ordem: 0 };

function ModalAuthAdmin({ titulo, onConfirm, onClose }) {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [erro, setErro]   = useState('');
  const [busy, setBusy]   = useState(false);
  const emailRef = useRef(null);

  useEffect(() => { emailRef.current?.focus(); }, []);

  const confirmar = async () => {
    if (!email || !senha) { setErro('Informe e-mail e senha.'); return; }
    setBusy(true);
    setErro('');
    try {
      await onConfirm(email, senha);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Credenciais inválidas.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={s.overlay} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className={s.authModal}>
        <div className={s.authHeader}>Autenticação de Administrador</div>
        <div className={s.authBody}>
          <p className={s.authDesc}>{titulo}</p>
          <label className={s.label}>
            E-mail do administrador
            <input ref={emailRef} className={s.input} type="email"
              value={email} onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && confirmar()} />
          </label>
          <label className={s.label}>
            Senha
            <input className={s.input} type="password"
              value={senha} onChange={e => setSenha(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && confirmar()} />
          </label>
          {erro && <div className={s.erro}>{erro}</div>}
        </div>
        <div className={s.authFooter}>
          <button className={s.btnPerigo} onClick={confirmar} disabled={busy}>
            {busy ? 'Verificando...' : 'Confirmar exclusão'}
          </button>
          <button className={s.btnSecundario} onClick={onClose} disabled={busy}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

export default function LocalEstoque() {
  const [lista, setLista]         = useState([]);
  const [form, setForm]           = useState({ ...VAZIO });
  const [editandoId, setEditandoId] = useState(null); // null = novo, number = editando
  const [modoForm, setModoForm]   = useState(false);  // true = form aberto
  const [erro, setErro]           = useState('');
  const [msg, setMsg]             = useState('');
  const [salvando, setSalvando]   = useState(false);
  const [authExcluir, setAuthExcluir] = useState(null); // { id, nome }
  const nomeRef = useRef(null);

  const carregar = async () => {
    try {
      const { data } = await api.get('/setores-estoque', { params: { todas: 'true' } });
      setLista(data);
    } catch { setErro('Erro ao carregar locais de estoque.'); }
  };

  useEffect(() => { carregar(); }, []);

  const abrirNovo = () => {
    setForm({ ...VAZIO });
    setEditandoId(null);
    setModoForm(true);
    setErro('');
    setMsg('');
    setTimeout(() => nomeRef.current?.focus(), 60);
  };

  const abrirEditar = (s) => {
    setForm({ nome: s.nome, sigla: s.sigla, ordem: s.ordem ?? 0 });
    setEditandoId(s.id);
    setModoForm(true);
    setErro('');
    setMsg('');
    setTimeout(() => nomeRef.current?.focus(), 60);
  };

  const fecharForm = () => { setModoForm(false); setErro(''); };

  const salvar = async () => {
    if (!form.nome.trim() || !form.sigla.trim()) {
      setErro('Nome e sigla são obrigatórios.'); return;
    }
    setSalvando(true);
    setErro('');
    try {
      if (editandoId === null) {
        await api.post('/setores-estoque', form);
        setMsg('Local de estoque criado com sucesso.');
      } else {
        const item = lista.find(i => i.id === editandoId);
        await api.put(`/setores-estoque/${editandoId}`, {
          ...form,
          ativo: item?.ativo ?? true,
        });
        setMsg('Local de estoque atualizado com sucesso.');
      }
      fecharForm();
      await carregar();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar.');
    } finally { setSalvando(false); }
  };

  const toggleAtivo = async (item) => {
    try {
      await api.patch(`/setores-estoque/${item.id}/toggle`);
      setMsg(`Local "${item.nome}" ${item.ativo ? 'desativado' : 'ativado'}.`);
      await carregar();
    } catch { setErro('Erro ao alterar status.'); }
  };

  const confirmarExclusao = async (email, senha) => {
    await api.delete(`/setores-estoque/${authExcluir.id}`, { data: { email, senha } });
    setMsg(`Local "${authExcluir.nome}" excluído com sucesso.`);
    setAuthExcluir(null);
    await carregar();
  };

  return (
    <div>
      <div className={styles.topbar}>
        <h2 className={styles.titulo}>Locais de Estoque</h2>
        <div className={styles.acoes}>
          <button className={styles.btnPrimario} onClick={abrirNovo} disabled={modoForm}>
            + Novo Local
          </button>
        </div>
      </div>

      {msg  && <div className={s.msgSucesso}>{msg}<button className={s.msgFechar} onClick={() => setMsg('')}>✕</button></div>}
      {erro && !modoForm && <div className={s.msgErro}>{erro}<button className={s.msgFechar} onClick={() => setErro('')}>✕</button></div>}

      {modoForm && (
        <div className={s.formCard}>
          <div className={s.formTitulo}>{editandoId === null ? 'Novo Local de Estoque' : 'Editar Local de Estoque'}</div>
          <div className={s.formRow}>
            <label className={s.label}>
              Nome *
              <input ref={nomeRef} className={s.input}
                value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && salvar()} />
            </label>
            <label className={s.label} style={{ maxWidth: 100 }}>
              Sigla *
              <input className={s.input}
                value={form.sigla}
                onChange={e => setForm(f => ({ ...f, sigla: e.target.value.toUpperCase() }))}
                maxLength={6}
                onKeyDown={e => e.key === 'Enter' && salvar()} />
            </label>
            <label className={s.label} style={{ maxWidth: 80 }}>
              Ordem
              <input className={s.input} type="number" min="0"
                value={form.ordem}
                onChange={e => setForm(f => ({ ...f, ordem: parseInt(e.target.value) || 0 }))}
                onKeyDown={e => e.key === 'Enter' && salvar()} />
            </label>
          </div>
          {erro && <div className={s.msgErro} style={{ marginTop: 4 }}>{erro}</div>}
          <div className={s.formAcoes}>
            <button className={styles.btnPrimario} onClick={salvar} disabled={salvando}>
              {salvando ? 'Salvando...' : 'Salvar'}
            </button>
            <button className={styles.btnSecundario} onClick={fecharForm}>Cancelar</button>
          </div>
        </div>
      )}

      <div className={s.tableWrap}>
        <table className={s.table}>
          <thead>
            <tr>
              <th className={s.thOrdem}>#</th>
              <th className={s.thSigla}>Sigla</th>
              <th>Nome</th>
              <th className={s.thStatus}>Status</th>
              <th className={s.thAcoes}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {lista.length === 0 && (
              <tr><td colSpan={5} className={s.vazio}>Nenhum local de estoque cadastrado.</td></tr>
            )}
            {lista.map(item => (
              <tr key={item.id} className={!item.ativo ? s.rowInativo : ''}>
                <td className={s.tdOrdem}>{item.ordem}</td>
                <td className={s.tdSigla}>{item.sigla}</td>
                <td>{item.nome}</td>
                <td className={s.tdStatus}>
                  <span className={item.ativo ? s.badgeAtivo : s.badgeInativo}>
                    {item.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
                <td className={s.tdAcoes}>
                  <button className={s.btnAcao} onClick={() => abrirEditar(item)}>Editar</button>
                  <button className={s.btnAcao} onClick={() => toggleAtivo(item)}>
                    {item.ativo ? 'Desativar' : 'Ativar'}
                  </button>
                  <button className={`${s.btnAcao} ${s.btnExcluir}`}
                    onClick={() => { setMsg(''); setErro(''); setAuthExcluir({ id: item.id, nome: item.nome }); }}>
                    Excluir
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {authExcluir && (
        <ModalAuthAdmin
          titulo={`Confirmar exclusão do local "${authExcluir.nome}"`}
          onConfirm={confirmarExclusao}
          onClose={() => setAuthExcluir(null)}
        />
      )}
    </div>
  );
}
